import type { ArticleLimited } from "@broadside/common";
import { CalendarToday } from "@mui/icons-material";
import { css, styled } from "@mui/material";
import clsx from "clsx";
import { format } from "date-fns";
import { formatDateRange, type ClassNameProp } from "../util";
import { LinkCard } from "./LinkCard";
import { TextOverImage } from "./TextOverImage";


const ArticleCardRoot = styled("div")(({ theme }) => css`
	display: flex;
	flex-direction: column;
	width: 100%;
	

	.linkCardTitle {
		font-size: 1.3rem;
		font-weight: bold;
		text-wrap: balance;
		line-height: 1.2;
	}
	
	.extraDetails {
		display: flex;
		flex-direction: column;
		margin-top: ${theme.spacing(.5)};
		gap: ${theme.spacing(.5)};
		
		* {
			line-height: 1;
		}
	}

	&.single {
		.linkCardTitle {
			font-size: 2rem;
		}
		
		.extraDetails {
			flex-direction: row;
			justify-content: space-between;
			margin-top: ${theme.spacing(1)};
		}
	}
	
	.startDate {
		position: absolute;
		top: 0;
		right: ${theme.spacing(2)};
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: ${theme.spacing(.5)};
		background-color: ${theme.palette.secondary.main};
		color: ${theme.palette.primary.contrastText};
		
		* {
			line-height: 1.1;
		}
		
		.day {
			font-size: 1.4rem;
			font-weight: bold;
		}
		.month {
			font-size: .9rem;
			font-weight: bold;
		}
		box-shadow: ${theme.shadows[1]};
	}
	
	.iconText {
		display: flex;
		align-items: center;
	}
`);

export type ArticleCardProps = {
	article: ArticleLimited;
	single?: boolean;
};
export const ArticleCard = ({ className, article, single }: ClassNameProp & ArticleCardProps) => {
	const textAlign = single ? "center" : undefined;
	const startDate = article.startDate && new Date(article.startDate);

	return (
		<LinkCard
			className={ clsx(className, { single }) }
			uri={ article.uri }
			image={ article.image }
			imageWidth={ single ? 800 : undefined }
			key={ article.uri }
		>
			<ArticleCardRoot className={ clsx({ single }) }>
				<TextOverImage
					className="linkCardTitle"
					component="span"
					width="100%"
					textAlign={ textAlign }
					fontSize={ single ? "2rem" : "1rem" }
					sx={{
						width: "100%",
						textAlign,
						fontSize: "4rem",
					}}
				>
					{ article.title }
				</TextOverImage>
				{ single && article.tagline && (
					<TextOverImage
						component="span"
						variant="body2"
						fontStyle="italic"
						width="100%"
						textAlign={ textAlign }
						sx={{ textWrap: "balance" }}
					>
						{ article.tagline }
					</TextOverImage>
				) }
				<div className="extraDetails">
					{ article.startDate && article.endDate && (
						<TextOverImage className="iconText" variant="body2">
							<CalendarToday fontSize="inherit" sx={{ marginRight: .5 }}/>
							{ formatDateRange(article.startDate, article.endDate) }
						</TextOverImage>
					) }

					{ article.location && (
						<TextOverImage className="iconText" variant={ single ? "body2" : "caption" }>
							{ article.location }
						</TextOverImage>
					) }
				</div>

				{ startDate && (
					<div className="startDate">
						<div className="day">
							{ format(startDate, "d") }
						</div>
						<div className="month">
							{ format(startDate, "MMM").toUpperCase() }
						</div>
					</div>
				) }
			</ArticleCardRoot>
		</LinkCard>
	);
};
